/**
 * CommunityProfilePage — the user's OWN profile (editor + premium view).
 *
 * Mirrors the visual language of CommunityUserProfilePage so a user sees
 * roughly the same shell others see, just with edit affordances.
 */

import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, Edit3, Save, ExternalLink, MessageSquare, Calendar, Award,
  Loader2, CheckCircle, X, Camera, Flame, Users, UserPlus, Sparkles,
  Target, BadgeCheck, Twitter, Globe, Eye,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { getCommunityProfile, updateCommunityProfile, getMyStreak, getMyCalls, getFollowStats } from '../services/api';

const AVATAR_GRADIENTS = [
  'from-rose-500 to-pink-600', 'from-purple-500 to-fuchsia-600',
  'from-violet-500 to-indigo-600', 'from-blue-500 to-cyan-600',
  'from-emerald-500 to-teal-600', 'from-amber-500 to-orange-600',
];

function gradientFor(name) {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_GRADIENTS[Math.abs(h) % AVATAR_GRADIENTS.length];
}

const ROLE_BADGE = {
  owner:      { label: 'Owner',   cls: 'bg-red-500/20 text-red-400 border-red-500/30' },
  superadmin: { label: 'S.Admin', cls: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  admin:      { label: 'Admin',   cls: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  mod:        { label: 'Mod',     cls: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  vip:        { label: 'VIP',     cls: 'bg-violet-500/20 text-violet-400 border-violet-500/30' },
};

const TIER_BADGE = {
  trial:   'bg-gray-500/15 text-gray-400 border-gray-500/30',
  starter: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  trader:  'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
  pro:     'bg-violet-500/15 text-violet-400 border-violet-500/30',
  elite:   'bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400 border-amber-500/30',
};

const BADGE_DEFS = {
  blue:  { label: 'Verified',         desc: 'Email confirmed + 30 days active', emoji: '🔵' },
  green: { label: 'Trusted Trader',   desc: '100+ trades, 60%+ win rate',       emoji: '🟢' },
  gold:  { label: 'Premium Member',   desc: 'Elite tier subscriber',            emoji: '🟡' },
  star:  { label: 'Top Contributor',  desc: 'Community voted most helpful',     emoji: '⭐' },
};

export default function CommunityProfilePage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [bio, setBio] = useState('');
  const [twitter, setTwitter] = useState('');
  const [website, setWebsite] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarData, setAvatarData] = useState(undefined);
  const [streak, setStreak] = useState(null);
  const [callStats, setCallStats] = useState(null);
  const [followStats, setFollowStats] = useState({ followersCount: 0, followingCount: 0 });
  // Mount-only loader visibility (max 5s) — independent of fetch effect.
  const [showLoader, setShowLoader] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setShowLoader(false), 5000);
    return () => clearTimeout(t);
  }, []);
  const fileInputRef = useRef(null);

  // Depend on user.userId (a stable string) — NOT the full user object,
  // whose reference changes on every AuthProvider token-refresh tick and
  // would otherwise re-fire all four API calls in flight, racing each
  // other and stalling the loading flag on slow networks.
  const uid = user?.userId;

  // Track which fetch is current. If `uid` changes (e.g. AuthProvider's
  // token refresh updates the user shape), the OLDER fetch could resolve
  // AFTER the newer one and overwrite real data with stale data. We tag
  // each fetch with an id and only commit responses from the latest one.
  const latestFetchRef = useRef(0);

  useEffect(() => {
    if (!uid) return;
    const fetchId = ++latestFetchRef.current;
    const isCurrent = () => fetchId === latestFetchRef.current;

    getCommunityProfile(uid)
      .then(d => {
        if (!isCurrent() || !d) return;
        setProfile(d);
        setBio(d.bio || '');
        setTwitter(d.twitter || '');
        setWebsite(d.website || '');
        if (d.avatar) setAvatarPreview(d.avatar);
      })
      .catch(() => {})
      .finally(() => { if (isCurrent()) setLoading(false); });

    getMyStreak().then(d => { if (isCurrent()) setStreak(d); }).catch(() => {});
    getMyCalls().then(d => { if (isCurrent()) setCallStats(d?.stats || null); }).catch(() => {});
    getFollowStats(uid).then(d => { if (isCurrent()) setFollowStats(d || { followersCount: 0, followingCount: 0 }); }).catch(() => {});
  }, [uid]);

  function showToast(msg, ms = 3000) {
    setToast(msg);
    setTimeout(() => setToast(null), ms);
  }

  function pickAvatar(file) {
    if (!file) return;
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 256; canvas.height = 256;
      const ctx = canvas.getContext('2d');
      const size = Math.min(img.width, img.height);
      const sx = (img.width - size) / 2;
      const sy = (img.height - size) / 2;
      ctx.drawImage(img, sx, sy, size, size, 0, 0, 256, 256);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      setAvatarPreview(dataUrl);
      setAvatarData(dataUrl);
      showToast('Avatar selected — click Save to apply');
    };
    img.src = URL.createObjectURL(file);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await updateCommunityProfile(bio, twitter, website, avatarData);
      setProfile(prev => ({ ...prev, bio, twitter, website, avatar: avatarData !== undefined ? avatarData : prev?.avatar }));
      setAvatarData(undefined);
      setEditing(false);
      showToast('Profile updated');
    } catch (err) {
      showToast(`Failed: ${err.message}`, 4000);
    }
    setSaving(false);
  }

  function cancelEdit() {
    setBio(profile?.bio || '');
    setTwitter(profile?.twitter || '');
    setWebsite(profile?.website || '');
    setAvatarPreview(profile?.avatar || null);
    setAvatarData(undefined);
    setEditing(false);
  }

  // No full-page loading guard — render the shell immediately so a hung
  // request can't trap the user on a spinner.
  const p = profile || {};
  const badges = p.badges || [];
  const achievements = p.achievements || [];
  const earned = achievements.filter(a => a.earned);
  const remaining = achievements.filter(a => !a.earned);
  const gradient = gradientFor(p.name);
  const tierCls = TIER_BADGE[p.tier] || TIER_BADGE.starter;
  const roleBadge = ROLE_BADGE[p.chatRole];
  const memberSinceLabel = p.stats?.memberSince
    ? new Date(p.stats.memberSince).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : '—';

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl">
      {/* Top loader — only visible for the first 5 seconds */}
      {showLoader && !profile && (
        <div className="bg-dark-800/40 rounded-2xl border border-dark-700/50 p-4 flex items-center justify-center gap-3">
          <Loader2 className="w-4 h-4 text-violet-400 animate-spin" />
          <span className="text-xs text-gray-400">Loading your profile…</span>
        </div>
      )}

      {/* ─── Premium Hero ──────────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-3xl border border-dark-700/50">
        <div className={`h-32 bg-gradient-to-br ${gradient} relative`}>
          <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-dark-900/40 to-transparent" />
        </div>
        <div className="bg-dark-800 px-6 pb-6 -mt-16 relative">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            {/* Avatar + identity */}
            <div className="flex items-end gap-4">
              <div className="relative group">
                {avatarPreview ? (
                  <img src={avatarPreview} alt={p.name}
                    className="w-28 h-28 rounded-2xl border-4 border-dark-800 shadow-2xl object-cover" />
                ) : (
                  <div className={`w-28 h-28 rounded-2xl border-4 border-dark-800 shadow-2xl bg-gradient-to-br ${gradient} flex items-center justify-center text-4xl font-bold text-white`}>
                    {(p.name || '?')[0].toUpperCase()}
                  </div>
                )}
                {editing && (
                  <>
                    <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
                      onChange={(e) => pickAvatar(e.target.files[0])} />
                    <button onClick={() => fileInputRef.current?.click()}
                      className="absolute inset-0 rounded-2xl bg-black/50 flex flex-col items-center justify-center cursor-pointer hover:bg-black/70 transition-all">
                      <Camera className="w-6 h-6 text-white" />
                      <span className="text-[10px] text-white mt-1 font-semibold">Change</span>
                    </button>
                  </>
                )}
              </div>
              <div className="pb-2">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <h1 className="text-2xl font-bold text-white">{p.name}</h1>
                  {callStats?.isVerifiedCaller && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                      <BadgeCheck className="w-3 h-3" /> VERIFIED CALLER
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${tierCls}`}>
                    {p.tier || 'starter'}
                  </span>
                  {roleBadge && (
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${roleBadge.cls}`}>
                      {roleBadge.label}
                    </span>
                  )}
                  <span className="text-[10px] text-gray-500 inline-flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Member since {memberSinceLabel}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 pb-2">
              {!editing && user?.userId && (
                <Link to={`/community/profile/${user.userId}`}
                  className="px-3 py-2 rounded-xl bg-dark-700 hover:bg-dark-600 text-gray-300 text-xs font-semibold flex items-center gap-1.5 border border-dark-600">
                  <Eye className="w-3.5 h-3.5" /> Public view
                </Link>
              )}
              {editing ? (
                <>
                  <button onClick={cancelEdit}
                    className="px-3 py-2 rounded-xl bg-dark-700 hover:bg-dark-600 text-gray-300 text-xs font-semibold border border-dark-600">
                    Cancel
                  </button>
                  <button onClick={handleSave} disabled={saving}
                    className="px-4 py-2 rounded-xl bg-violet-500 hover:bg-violet-400 disabled:opacity-50 text-white text-sm font-bold flex items-center gap-1.5">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
                  </button>
                </>
              ) : (
                <button onClick={() => setEditing(true)}
                  className="px-4 py-2 rounded-xl bg-violet-500 hover:bg-violet-400 text-white text-sm font-bold flex items-center gap-1.5">
                  <Edit3 className="w-3.5 h-3.5" /> Edit profile
                </button>
              )}
            </div>
          </div>

          {/* Bio (read or edit) */}
          {editing ? (
            <div className="mt-5 space-y-3">
              <div>
                <label className="block text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">Bio (200 chars)</label>
                <textarea value={bio} onChange={e => setBio(e.target.value)} maxLength={200} rows={3}
                  placeholder="Tell the community about yourself..."
                  className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:border-violet-400 focus:outline-none resize-none" />
                <p className="text-[10px] text-gray-600 mt-1 text-right">{bio.length}/200</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">Twitter</label>
                  <input type="text" value={twitter} onChange={e => setTwitter(e.target.value)} maxLength={30}
                    placeholder="username"
                    className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:border-violet-400 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">Website</label>
                  <input type="text" value={website} onChange={e => setWebsite(e.target.value)} maxLength={100}
                    placeholder="https://..."
                    className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:border-violet-400 focus:outline-none" />
                </div>
              </div>
            </div>
          ) : (
            <>
              {p.bio && (
                <p className="text-sm text-gray-300 mt-4 leading-relaxed max-w-2xl">{p.bio}</p>
              )}
              {!p.bio && (
                <p className="text-sm text-gray-600 italic mt-4">No bio yet — click Edit profile to introduce yourself.</p>
              )}
              {(p.twitter || p.website) && (
                <div className="flex gap-3 mt-3">
                  {p.twitter && (
                    <a href={`https://twitter.com/${p.twitter}`} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1">
                      <Twitter className="w-3.5 h-3.5" /> @{p.twitter}
                    </a>
                  )}
                  {p.website && (
                    <a href={p.website.startsWith('http') ? p.website : `https://${p.website}`} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-gray-400 hover:text-gray-300 inline-flex items-center gap-1">
                      <Globe className="w-3.5 h-3.5" /> {p.website}
                    </a>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ─── KPI Strip ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI icon={Users}          label="Followers"  value={followStats.followersCount} color="violet" />
        <KPI icon={UserPlus}       label="Following"  value={followStats.followingCount} color="cyan" />
        <KPI icon={MessageSquare}  label="Messages"   value={p.stats?.messageCount || 0} color="emerald" />
        <KPI icon={Sparkles}       label="Reputation" value={p.stats?.reputation || 0}   color="amber" />
      </div>

      {/* ─── Verified Caller Stats ─────────────────────────────────────── */}
      {callStats && callStats.totalCalls > 0 && (
        <div className="bg-gradient-to-br from-emerald-500/10 via-dark-800/80 to-dark-900 rounded-2xl border border-emerald-500/20 p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-emerald-400" />
              <h3 className="text-sm font-bold text-white">Trading Call Performance</h3>
              {callStats.isVerifiedCaller && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                  VERIFIED CALLER ✓
                </span>
              )}
            </div>
            <p className="text-[10px] text-gray-500">{callStats.resolvedCalls || 0} of {callStats.totalCalls} resolved</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-dark-700/40 rounded-xl p-3">
              <p className="text-[10px] text-gray-500 uppercase">Total Calls</p>
              <p className="text-xl font-bold text-white">{callStats.totalCalls}</p>
            </div>
            <div className="bg-dark-700/40 rounded-xl p-3">
              <p className="text-[10px] text-gray-500 uppercase">Accuracy</p>
              <p className={`text-xl font-bold ${callStats.accuracy == null ? 'text-gray-500' : callStats.accuracy >= 60 ? 'text-emerald-400' : callStats.accuracy >= 40 ? 'text-amber-400' : 'text-red-400'}`}>
                {callStats.accuracy == null ? '—' : `${Math.round(callStats.accuracy)}%`}
              </p>
            </div>
            <div className="bg-dark-700/40 rounded-xl p-3">
              <p className="text-[10px] text-gray-500 uppercase">Win / Loss</p>
              <p className="text-xl font-bold">
                <span className="text-emerald-400">{callStats.hits ?? 0}</span>
                <span className="text-gray-600 mx-1">/</span>
                <span className="text-red-400">{callStats.misses ?? 0}</span>
              </p>
            </div>
            <div className="bg-dark-700/40 rounded-xl p-3">
              <p className="text-[10px] text-gray-500 uppercase">Avg Return</p>
              <p className={`text-xl font-bold ${(callStats.avgReturn ?? callStats.avgPercentGain ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {(callStats.avgReturn ?? callStats.avgPercentGain) == null ? '—' :
                  `${(callStats.avgReturn ?? callStats.avgPercentGain) > 0 ? '+' : ''}${Number(callStats.avgReturn ?? callStats.avgPercentGain).toFixed(1)}%`}
              </p>
            </div>
          </div>
          {!callStats.isVerifiedCaller && callStats.totalCalls > 0 && (
            <div className="mt-4 bg-dark-700/30 rounded-xl p-3 border border-dark-700/50">
              <p className="text-[10px] text-gray-500">Verified Caller requires 20+ resolved calls, 60%+ accuracy, 5%+ avg return</p>
              <div className="mt-1.5 bg-dark-700 rounded-full h-1.5">
                <div className="bg-emerald-400 rounded-full h-1.5 transition-all"
                  style={{ width: `${Math.min(100, ((callStats.resolvedCalls || 0) / 20) * 100)}%` }} />
              </div>
              <p className="text-[9px] text-gray-600 mt-1">{callStats.resolvedCalls || 0}/20 resolved calls</p>
            </div>
          )}
        </div>
      )}

      {/* ─── Verification Badges ───────────────────────────────────────── */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
          <Shield className="w-4 h-4" /> Verification Badges
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(BADGE_DEFS).map(([key, def]) => {
            const isEarned = badges.some(b => b.id === key);
            return (
              <div key={key} className={`rounded-xl p-4 border transition-all ${
                isEarned
                  ? 'bg-gradient-to-br from-amber-500/5 to-orange-500/5 border-amber-500/20'
                  : 'bg-dark-700/30 border-dark-600/30 opacity-40'
              }`}>
                <span className="text-2xl block mb-1">{def.emoji}</span>
                <p className={`text-xs font-bold ${isEarned ? 'text-white' : 'text-gray-500'}`}>{def.label}</p>
                <p className="text-[10px] text-gray-600 mt-0.5">{def.desc}</p>
                {isEarned && <CheckCircle className="w-3.5 h-3.5 text-emerald-400 mt-2" />}
              </div>
            );
          })}
        </div>
      </div>

      {/* ─── Streak ─────────────────────────────────────────────────────── */}
      {streak && (
        <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
          <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
            <Flame className="w-4 h-4 text-orange-400" /> Daily Streak
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <div className="bg-dark-700/50 rounded-xl p-3 text-center">
              <p className="text-[10px] text-gray-500 uppercase">Current</p>
              <p className={`text-xl font-bold flex items-center justify-center gap-1 ${
                streak.currentStreak >= 100 ? 'text-yellow-300' :
                streak.currentStreak >= 30 ? 'text-orange-400' :
                streak.currentStreak >= 7 ? 'text-amber-400' :
                streak.currentStreak >= 1 ? 'text-orange-300' : 'text-gray-500'
              }`}>
                {streak.currentStreak > 0 && <Flame className="w-5 h-5" />}
                {streak.currentStreak}
              </p>
            </div>
            <div className="bg-dark-700/50 rounded-xl p-3 text-center">
              <p className="text-[10px] text-gray-500 uppercase">Longest</p>
              <p className="text-xl font-bold text-white">{streak.longestStreak}</p>
            </div>
            <div className="bg-dark-700/50 rounded-xl p-3 text-center">
              <p className="text-[10px] text-gray-500 uppercase">Total Days</p>
              <p className="text-xl font-bold text-violet-400">{streak.totalActiveDays}</p>
            </div>
            <div className="bg-dark-700/50 rounded-xl p-3 text-center">
              <p className="text-[10px] text-gray-500 uppercase">Milestones</p>
              <p className="text-xl font-bold text-amber-400">{(streak.milestonesEarned || []).length}</p>
            </div>
          </div>

          {streak.milestones && streak.milestones.length > 0 && (
            <div className="mb-5">
              <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Milestone Badges</p>
              <div className="flex flex-wrap gap-2">
                {streak.milestones.map(m => {
                  const isEarned = (streak.milestonesEarned || []).includes(m.days);
                  return (
                    <div key={m.days} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border ${
                      isEarned
                        ? m.tier === 'legendary' ? 'bg-gradient-to-r from-orange-500/20 to-yellow-500/20 text-yellow-300 border-yellow-500/30'
                        : m.tier === 'epic' ? 'bg-orange-500/15 text-orange-400 border-orange-500/25'
                        : m.tier === 'rare' ? 'bg-amber-500/15 text-amber-400 border-amber-500/25'
                        : 'bg-orange-500/10 text-orange-300 border-orange-500/20'
                        : 'bg-dark-700/30 text-gray-600 border-dark-700/50 opacity-40'
                    }`}>
                      <span>{m.emoji}</span> {m.name}
                      {isEarned && <CheckCircle className="w-3 h-3 ml-0.5" />}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {streak.activityHistory && streak.activityHistory.length > 0 && (() => {
            const today = new Date();
            const days = [];
            for (let i = 364; i >= 0; i--) {
              const d = new Date(today);
              d.setDate(d.getDate() - i);
              const dateStr = d.toISOString().split('T')[0];
              days.push({ date: dateStr, active: streak.activityHistory.some(a => a.date === dateStr) });
            }
            const weeks = [];
            for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
            return (
              <div>
                <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Activity — Last 365 Days</p>
                <div className="flex gap-[2px] overflow-x-auto pb-1">
                  {weeks.map((week, wi) => (
                    <div key={wi} className="flex flex-col gap-[2px]">
                      {week.map(day => (
                        <div key={day.date} title={`${day.date}${day.active ? ' — Active' : ''}`}
                          className={`w-[10px] h-[10px] rounded-[2px] ${day.active ? 'bg-amber-400' : 'bg-dark-700/50'}`} />
                      ))}
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-2 text-[9px] text-gray-600">
                  <span>Less</span>
                  <div className="w-[10px] h-[10px] rounded-[2px] bg-dark-700/50" />
                  <div className="w-[10px] h-[10px] rounded-[2px] bg-amber-400" />
                  <span>More</span>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ─── Achievements ──────────────────────────────────────────────── */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
            <Award className="w-4 h-4" /> Achievements
          </h3>
          <p className="text-[11px] text-gray-500">{earned.length} of {achievements.length} unlocked</p>
        </div>
        {earned.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 mb-4">
            {earned.map(a => (
              <div key={a.id} className="bg-violet-500/10 border border-violet-500/30 rounded-xl p-3 text-center" title={a.desc}>
                <span className="text-3xl block">{a.emoji}</span>
                <p className="text-[10px] font-semibold text-white mt-1.5">{a.name}</p>
              </div>
            ))}
          </div>
        )}
        {remaining.length > 0 && (
          <>
            <p className="text-[10px] text-gray-600 uppercase tracking-wider mb-2">Locked</p>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
              {remaining.map(a => (
                <div key={a.id} className="bg-dark-700/30 border border-dark-700/50 rounded-xl p-3 text-center opacity-40" title={a.desc}>
                  <span className="text-3xl block grayscale">{a.emoji}</span>
                  <p className="text-[10px] font-semibold text-gray-500 mt-1.5">{a.name}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-medium bg-dark-700 text-white border border-dark-600">
          {toast}
        </div>
      )}
    </div>
  );
}

function KPI({ icon: Icon, label, value, color = 'violet' }) {
  const colors = {
    violet:  'from-violet-500/10 to-purple-500/10 border-violet-500/20 text-violet-400',
    cyan:    'from-cyan-500/10 to-blue-500/10 border-cyan-500/20 text-cyan-400',
    emerald: 'from-emerald-500/10 to-teal-500/10 border-emerald-500/20 text-emerald-400',
    amber:   'from-amber-500/10 to-orange-500/10 border-amber-500/20 text-amber-400',
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} rounded-2xl p-4 border`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5" />
        <p className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
      </div>
      <p className="text-2xl font-bold text-white">{typeof value === 'number' ? value.toLocaleString() : value}</p>
    </div>
  );
}
